import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  ArrowRight,
  Target,
  Heart,
  Users,
  Lightbulb,
  Facebook,
  Instagram,
  Linkedin,
  Twitter,
} from "lucide-react";
import SEO from "../components/SEO";
import StructuredData from "../components/StructuredData";
import AnimateOnScroll from "../components/AnimateOnScroll";

const About = () => {
  return (
    <div>
      <SEO 
        title="About Us — Healthcare Operations Consultants"
        description="We're a team of healthcare operations consultants who've spent 15+ years helping clinics, private practices, and healthcare startups run better. Atlanta-based, serving the US and Canada."
        ogImage="https://customer-assets.emergentagent.com/job_e6aa7c2c-d03d-4974-9cb2-630ed0a0b376/artifacts/1n8hynym_pexels-cristian-rojas-8460371.jpg"
        keywords="healthcare consulting firm Atlanta, medical practice consultants, who are RNB Consulting, healthcare business advisors, clinic operations consultants, about healthcare consulting company"
      />
      <StructuredData data={[
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.rnbconsultinggroup.com" },
            { "@type": "ListItem", "position": 2, "name": "About Us", "item": "https://www.rnbconsultinggroup.com/about" }
          ]
        },
        {
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": "https://www.rnbconsultinggroup.com/#business",
          "name": "RNB Consulting Group",
          "description": "Boutique healthcare consulting firm with 15+ years of expertise in operations, workforce development, and patient experience strategy. Atlanta-based, serving the US and Canada.",
          "url": "https://www.rnbconsultinggroup.com/about",
          "telephone": "(888) 250-4605",
          "email": "info@rnbconsultinggroup.com",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Atlanta",
            "addressRegion": "GA",
            "addressCountry": "US"
          },
          "founder": [
            { "@type": "Person", "name": "Breana Payne" },
            { "@type": "Person", "name": "Rory Sparrow Jr." }
          ],
          "sameAs": [
            "https://www.facebook.com/112675061860270",
            "https://www.instagram.com/rnbconsultinggroup/",
            "https://www.linkedin.com/company/rnb-consulting-group",
            "https://www.x.com/rnb_cgroup"
          ]
        }
      ]} />
      
      {/* Hero Section */}
      <section className="relative py-28 bg-gradient-to-br from-rnb-light to-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-rnb-blue/5 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <AnimateOnScroll>
            <div className="text-center max-w-3xl mx-auto">
              <div className="inline-flex items-center px-4 py-2 bg-rnb-blue/10 rounded-full mb-6">
                <span className="text-sm font-medium text-rnb-navy">About RNB Consulting Group</span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-rnb-navy mb-6">
                We Help Healthcare Practices Thrive
              </h1>
              <p className="text-lg text-rnb-gray mb-8">
                We get it—running a medical practice is about so much more than medicine. 
                That&apos;s why we&apos;re here: to help you handle the business side so you can 
                focus on what matters most—your patients.
              </p>
              <Link to="/contact">
                <Button size="lg" className="btn-hover-lift" data-testid="about-cta-btn">
                  Let&apos;s Chat
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </AnimateOnScroll>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-lg text-rnb-gray leading-relaxed">
            At <strong className="text-rnb-navy">RNB Consulting Group</strong>, we know that 
            starting or growing a healthcare practice isn't easy. Small practices and early-stage 
            organizations face unique pressures: limited resources, staffing challenges, compliance 
            hurdles, and the constant demand to deliver excellent patient care while trying to stay 
            financially sustainable.
          </p>
          <p className="text-lg text-rnb-gray leading-relaxed mt-4">
            <strong className="text-rnb-navy">We exist to give healthcare leaders the clarity, 
            structure, and confidence they need to grow without burning out.</strong>
          </p>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimateOnScroll direction="left">
              <div>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-rnb-navy mb-6">
                  Who We Are
                </h2>
                <div className="space-y-4 text-rnb-gray">
                  <p>
                    RNB Consulting Group is a boutique healthcare consulting firm with deep expertise 
                    in operations, workforce development, and patient experience strategy. We've 
                    partnered with practices and startups across the healthcare spectrum to build 
                    stronger systems, more resilient teams, and growth plans that actually work in the 
                    real world.
                  </p>
                  <p>
                    Unlike large consulting firms that push cookie-cutter solutions, we pride ourselves 
                    on a <strong className="text-rnb-navy">hands-on, people-first approach</strong>. 
                    We listen, we learn about your business, and we design strategies that fit your 
                    goals, your culture, and your patients.
                  </p>
                </div>
              </div>
            </AnimateOnScroll>

            <AnimateOnScroll direction="right">
              <div className="relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_e6aa7c2c-d03d-4974-9cb2-630ed0a0b376/artifacts/1n8hynym_pexels-cristian-rojas-8460371.jpg"
                  alt="Healthcare team of providers standing together in a clinic exam room"
                  className="img-warm w-full"
                />
              </div>
            </AnimateOnScroll>
          </div>
        </div>
      </section>

      {/* Why We Do It */}
      <section className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimateOnScroll direction="left">
              <div className="order-2 lg:order-1">
                <img
                  src="https://customer-assets.emergentagent.com/job_e6aa7c2c-d03d-4974-9cb2-630ed0a0b376/artifacts/ijqokk5b_Hospital-Consulting.jpg"
                  alt="Diverse team of healthcare professionals collaborating and laughing together"
                  className="img-warm w-full"
                />
              </div>
            </AnimateOnScroll>

            <AnimateOnScroll direction="right">
              <div className="order-1 lg:order-2">
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-rnb-navy mb-6">
                  Why We Do It
                </h2>
                <div className="space-y-4 text-rnb-gray">
                  <p>
                    Because healthcare leaders deserve more than just "getting by." We've seen too 
                    many small practices struggle to keep their doors open or startups fail to move 
                    past the launch stage. It's not because there's a lack of passion. It's because 
                    they didn't have the right support.
                  </p>
                  <p>
                    <strong className="text-rnb-navy">Our mission is to change that.</strong> We're 
                    here to be a trusted partner in your growth journey, helping you create a healthcare 
                    organization where operations run smoothly, employees feel empowered, and patients 
                    get the care they deserve.
                  </p>
                </div>
              </div>
            </AnimateOnScroll>
          </div>
        </div>
      </section>

      {/* Founders Note */}
      <section className="py-28 bg-rnb-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-white/10 backdrop-blur border-0">
            <CardContent className="p-8 md:p-12">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-6">
                A Note From Our Founders
              </h2>
              <blockquote className="text-slate-200 text-lg leading-relaxed mb-6">
                "We started RNB Consulting Group because we believe small healthcare practices and 
                startups deserve the same level of strategic support as large organizations but 
                delivered in a way that's approachable, actionable, affordable and aligned with 
                their reality. Every practice has the potential to thrive, and our role is to help 
                unlock that potential."
              </blockquote>
              <p className="text-rnb-teal font-semibold">
                — Breana Payne & Rory Sparrow Jr.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Social Links */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl font-bold text-rnb-navy mb-8">
            Check Out Our Socials
          </h2>
          <div className="flex justify-center gap-6">
            <a
              href="https://www.facebook.com/112675061860270"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 bg-rnb-light rounded-full flex items-center justify-center text-rnb-blue hover:bg-rnb-blue hover:text-white transition-colors"
            >
              <Facebook className="w-5 h-5" />
            </a>
            <a
              href="https://www.instagram.com/rnbconsultinggroup/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 bg-rnb-light rounded-full flex items-center justify-center text-rnb-blue hover:bg-rnb-blue hover:text-white transition-colors"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a
              href="https://www.linkedin.com/company/rnb-consulting-group"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 bg-rnb-light rounded-full flex items-center justify-center text-rnb-blue hover:bg-rnb-blue hover:text-white transition-colors"
            >
              <Linkedin className="w-5 h-5" />
            </a>
            <a
              href="https://www.x.com/rnb_cgroup"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 bg-rnb-light rounded-full flex items-center justify-center text-rnb-blue hover:bg-rnb-blue hover:text-white transition-colors"
            >
              <Twitter className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Ready to Grow */}
      <section className="py-28 bg-rnb-light">
        <AnimateOnScroll>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-rnb-navy mb-6">
              Ready to Grow?
            </h2>
            <p className="text-rnb-gray text-lg mb-8">
              Whether you're opening your first practice, expanding into new locations, or simply 
              trying to make your day-to-day operations more manageable, RNB Consulting Group is 
              here to help. Contact us today to start building the foundation for your future success.
            </p>
            <Link to="/contact">
              <Button size="lg" className="btn-hover-lift" data-testid="about-bottom-cta">
                Contact Us
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </AnimateOnScroll>
      </section>
    </div>
  );
};

export default About;
